/**
 * Error alert component for displaying standardized errors
 *
 * This component displays errors in a user-friendly alert format
 * with appropriate styling, actions, and technical details.
 */

import type { BaseError } from "@charphq/errors";
import {
  getUserFriendlyMessage,
  getErrorAction,
  getErrorCategoryName,
  shouldShowRetryButton,
  shouldShowContactSupport,
  getErrorColor,
} from "@charphq/errors";
import { Alert, AlertDescription } from "../base/alert";
import { Button } from "../base/button";

/**
 * Props for error alert component
 */
export interface ErrorAlertProps {
  error: BaseError;
  onRetry?: () => void;
  onDismiss?: () => void;
  onContactSupport?: () => void;
  showDetails?: boolean;
  className?: string;
}

/**
 * Error alert component with icon and action
 */
export function ErrorAlert({
  error,
  onRetry,
  onDismiss,
  onContactSupport,
  showDetails = false,
  className = "",
}: ErrorAlertProps): React.JSX.Element {
  const message = getUserFriendlyMessage(error);
  const action = getErrorAction(error);
  const categoryName = getErrorCategoryName(error);
  // const icon = getErrorIcon(error);
  const color = getErrorColor(error);
  const canRetry = shouldShowRetryButton(error);
  const canContactSupport = shouldShowContactSupport(error);

  // Map error colors to alert variants
  const getAlertVariant = (color: string) => {
    switch (color) {
      case "red":
        return "destructive";
      case "orange":
        return "destructive";
      case "yellow":
        return "default";
      case "blue":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <Alert variant={getAlertVariant(color)} className={className}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {/* Icon placeholder - replace with your icon component */}
          <div className="w-5 h-5">⚠️</div>
        </div>

        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium">{categoryName}</h3>

          <AlertDescription className="mt-1">
            <p>{message}</p>
            {action && <p className="mt-1 text-xs opacity-75">{action}</p>}
          </AlertDescription>

          {showDetails && error.context && (
            <details className="mt-2">
              <summary className="text-xs text-muted-foreground cursor-pointer">
                Technical Details
              </summary>
              <pre className="mt-1 text-xs text-muted-foreground bg-muted p-2 rounded overflow-auto">
                {JSON.stringify(error.context, null, 2)}
              </pre>
            </details>
          )}

          <div className="mt-3 flex space-x-2">
            {canRetry && onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                Try Again
              </Button>
            )}

            {canContactSupport && onContactSupport && (
              <Button variant="outline" size="sm" onClick={onContactSupport}>
                Contact Support
              </Button>
            )}

            {onDismiss && (
              <Button variant="ghost" size="sm" onClick={onDismiss}>
                Dismiss
              </Button>
            )}
          </div>
        </div>
      </div>
    </Alert>
  );
}
